<script lang="ts">
	//Design system components
	import {
		Alert,
		Button,
		CardGrid,
		Container,
		Icon,
		IconButton,
		Input,
		Modal,
		Text,
	} from '@Shelly/shelly-design-system';
	//Services
	import { format } from 'svelte-i18n';
	//Stores
	import { user_data } from 'src/stores/account/user';
	//Helpers
	import { copy } from 'src/helpers/copy_text';
	import { editLiveTariff } from 'src/helpers/power_prices_helpers';
	//Svelte hooks
	import { createEventDispatcher } from 'svelte';
	//Components
	import LiveTariffInstruction from './LiveTariffInstruction.svelte';

	//TODO: Add translation keys for all live tariff changes!

	export let renewToken,
		liveTariff,
		showLiveTariffInfo = true;

	$: url = `/v2/users/pp-ltu/${liveTariff?.token}`;

	const host = $user_data.user_api_url;
	const dispatch = createEventDispatcher();

	$: price = liveTariff?.price;
	$: name = liveTariff?.name;
	$: currency = liveTariff?.currency;

	let showTariffInstruction = false;
	/**
	 * Update live tariff name and price
	 * @param {Event} e event
	 * @param {string} prop property to update
	 */
	const changeLiveTariff = (e, prop) => {
		liveTariff[prop] = e.target.value;
	};

	/**
	 * Enable renew token
	 */
	const enableRenew = () => {
		renewToken = true;
		dispatch('renew');
	};

	/**
	 * If renew token is enabled, update the local state with the latest values
	 */
	$: if (renewToken) {
		if (liveTariff?.name != name || liveTariff?.price != price) {
			name = liveTariff.name;
			price = liveTariff.price;
		}
	}

	/**
	 * Validate tariff name length
	 * @param {Event} e event
	 */
	const validateValueLength = (e) => {
		if (e.target.value.length > 32) {
			e.target.value = e.target.value.slice(0, 32); // limit tariff name to 32 chars
		}
	};
</script>

{#if showLiveTariffInfo}
	<Alert
		dismissable
		on:dismissable_click={() => (showLiveTariffInfo = false)}
		class="mb-m"
		type="info"
		heading={$format('live_tariff_desc', {
			default:
				'Live tariff can be updated with a minimum reasonable time interval(once per        minute) via an API call including the API url.',
		})}
		cta={{
			title: `${$format('label_how_to_setup_live_tariff', {
				default: 'How To Set Up Live Tariff ',
			})}`,
		}}
		on:cta_click={() => (showTariffInstruction = true)}
	/>
{/if}

{#key name || price || currency}
	<div class="d-flex">
		<Container class="mb-m" type="borderSolid" padding="xs">
			<div class="content-background dark d-flex justify-content-start">
				<Icon class="mr-s" icon="fas fa-coins" size="s" />
				<Text class="text-bold" styleType="controls-small" element="h3">
					{$format('price_title_live_tariff', {
						default: '{name}: {price} {currency}',
						values: { name, price, currency },
					})}
				</Text>
			</div>
		</Container>
	</div>
{/key}

<Text styleType="body-small" class=" mb-s mb-s text-bold" element="p"
	>{$format('title_set_name_and_price_manually', {
		default: `Set name and price manually`,
	})}</Text
>

<CardGrid md={150} sm={150} class="pb-0">
	<div class="card">
		<Input
			aria-labelledby="input-14trtlf"
			label={$format('placeholder_tariff_name', { default: 'Tariff name' })}
			icon="fas fa-plug-circle-bolt"
			value={name}
			on:change={(e) => changeLiveTariff(e, 'name')}
			on:input={(e) => validateValueLength(e)}
			id="input-14trtlf"
		/>
	</div>
	<div class="card width-sm-full width-md-auto">
		<Input
			aria-labelledby="input-etkq0pz"
			icon="fas fa-coins"
			type="number"
			label={$format('placeholder_tariff_price', {
				default: 'Price',
			})}
			value={price}
			on:change={(e) => changeLiveTariff(e, 'price')}
			id="input-etkq0pz"
		/>
	</div>
	<div class="card width-sm-full width-md-auto">
		<Button size="m" on:click={() => editLiveTariff({ ...liveTariff, price, name })}>
			{$format('button_save', { default: 'Save' })}
		</Button>
	</div>
</CardGrid>

<hr class="mt-m mb-m" />

<Text class="mb-xs" element="h2"
	>{$format('api_url_title', {
		default: `API URL`,
	})}</Text
>
<Text styleType="body-small" class="mb-s text-200"
	>{$format('live_tariff_url_desc', {
		default: 'With the tokenized url you can create a script for updating your {name}.',
		values: { name },
	})}
</Text>
<Container class="width-xl mb-s" type="borderSolid" padding="xs">
	<div class="content-background dark d-flex justify-content-start align-items-center">
		<Icon class="mr-s" size="s" icon="fas fa-link" />
		<Text noWrap class="link-styles text-small" styleType="body-small">
			{host + url}
		</Text>
		<IconButton
			icon="fas fa-copy"
			size="m"
			width="s"
			on:click={() =>
				copy(host + url, $format('copied_successfully', { default: 'Copied successfully!' }))}
			>{$format('button_copy_url', { default: 'Copy url' })}</IconButton
		>
	</div>
</Container>

<Button variant="secondary" size="m" width="s" on:click={enableRenew}
	>{$format('button_renew_token', { default: 'Renew token' })}</Button
>

<!-- <Modal
	bind:showModal={showLiveTariffInfo}
	on:close={() => (showLiveTariffInfo = false)}
	title={$format('title_live_tariff', { default: 'Live tariff' })}
>
	<svelte:fragment slot="content">
		<Text class="mb-s text-200"
			>{$format('live_tariff_desc_one', {
				default: `Live tariff can be updated within a minimum reasonable time interval(once per minute) via an API call including the API url.`,
			})}</Text
		>
		<Text class="mb-s text-200"
			>{$format('live_tariff_desc_two', {
				default: `API url - with the tokenized url you can create a script or use other tools for updating your Live tariff.`,
			})}</Text
		>
		<Text class="mb-s text-200"
			>{$format('live_tariff_desc_three', {
				default: `Required parameters are: tokenized url and price.`,
			})}</Text
		>
	</svelte:fragment>
</Modal> -->

{#if showTariffInstruction}
	<LiveTariffInstruction
		bind:showTariffInstruction
		{name}
		{url}
		{host}
		token={liveTariff?.token}
		on:close={() => (showTariffInstruction = false)}
	/>
{/if}
